import { Layout } from '../components/layout/Layout';

export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">PRIVACY POLICY</h1>
          <p className="text-xl font-semibold text-primary">NEXTGIG</p>
          <p className="text-muted-foreground mt-2">Last Updated: 31st March 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          
          {/* Section 1 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">1. INTRODUCTION</h2>
            <p className="text-muted-foreground leading-relaxed">
              NEXTGIG ("Platform," "we," "us," or "our") is committed to protecting your privacy.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website, mobile applications, and services.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              By using the Platform, you agree to the terms of this Privacy Policy.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">2. SCOPE</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              This Privacy Policy applies to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Visitors browsing the Platform.</li>
              <li>Users purchasing tickets ("Attendees")</li>
              <li>Event organizers ("Organizers")</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">3. INFORMATION WE COLLECT</h2>
            
            <h3 className="text-lg font-semibold mb-3 mt-6">3.1 Information You Provide</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We collect information you provide directly, including:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Name, email address, phone number</li>
              <li>Account login credentials</li>
              <li>Billing and payment information</li>
              <li>Event registration details</li>
              <li>Organizer business information (e.g., Company name, tax ID)</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3 mt-6">3.2 Payment Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              Payments are processed by third-party providers, including Stripe and Paystack.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3 mb-3">
              We do not store full Debit/Credit card details. Payment processors may collect:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Card number</li>
              <li>Expiry date</li>
              <li>Billing address</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              These are handled in accordance with their own privacy policies.
            </p>

            <h3 className="text-lg font-semibold mb-3 mt-6">3.3 Cookies & Tracking Technologies</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Authenticate users.</li>
              <li>Remember preferences.</li>
              <li>Analyze usage and performance.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              You can control cookies through your browser settings.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">4. HOW WE USE YOUR INFORMATION</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use your information to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Provide and operate the Platform.</li>
              <li>Process ticket purchases and payments</li>
              <li>Communicate with you any transactions and updates.</li>
              <li>Manage events and organizer accounts.</li>
              <li>Prevent fraud and enhance security.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">5. SHARING OF INFORMATION</h2>
            
            <h3 className="text-lg font-semibold mb-3 mt-6">5.1 With Event Organizers</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              When you purchase a ticket, your information may be shared with the Organizer for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Event management</li>
              <li>Entry verification</li>
              <li>Customer support</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3 mt-6">5.2 With Service Providers</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We share information with trusted third parties, including:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Payment processors (e.g., Stripe, Paystack)</li>
              <li>Cloud hosting providers</li>
              <li>Analytics services</li>
              <li>Customer support tools</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              These providers are obligated to protect your data.
            </p>

            <h3 className="text-lg font-semibold mb-3 mt-6">5.3 Legal Requirements</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We may disclose your information if required to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Comply with laws or regulations</li>
              <li>Respond to legal requests or court orders</li>
              <li>Protect rights, safety, and security</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">6. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and processed in countries outside Canada, including the United States and other jurisdictions.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We take reasonable steps to ensure your data is protected in accordance with applicable laws.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">7. DATA RETENTION</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We retain personal information only as long as necessary to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Provide services.</li>
              <li>Comply with legal and tax obligations.</li>
              <li>Resolve disputes.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">8. YOUR RIGHTS (CANADA)</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Depending on applicable laws, you may have the right to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Access your personal information.</li>
              <li>Request correction of inaccurate data</li>
              <li>Withdraw consent (where applicable)</li>
              <li>Request deletion of your data (subject to legal limitations)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To exercise these rights, contact us using the details below.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">9. SECURITY</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We implement reasonable technical and organizational measures to protect your data, including:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Encryption of sensitive information</li>
              <li>Secure servers and access controls</li>
              <li>Monitoring for unauthorized access</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              However, no system is completely secure.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">10. CHILDREN'S PRIVACY</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Platform is not intended for individuals under 13 years old.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We do not knowingly collect personal information from children.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">11. THIRD-PARTY LINKS</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Platform may contain links to third-party websites.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We are not responsible for the privacy practices of those sites.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">12. ORGANIZER DATA RESPONSIBILITY</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Organizers who collect attendee data through the Platform agree to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Use personal data only for event-related purposes.</li>
              <li>Comply with applicable privacy laws.</li>
              <li>Not misuse or sell attendee data.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">13. CHANGES TO THIS PRIVACY POLICY</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We will notify users of significant changes. Continued use of the Platform constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">14. CONTACT US</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have questions or concerns about this Privacy Policy:
            </p>
            <div className="bg-muted/50 rounded-lg p-6 space-y-2">
              <p className="font-semibold text-foreground">NEXTGIG</p>
              <p className="text-muted-foreground">
                Email: <a href="mailto:Nextgigsuite@gmail.com" className="text-primary hover:underline">Nextgigsuite@gmail.com</a>
              </p>
              <p className="text-muted-foreground">
                Address: 8, Baba Ahmed Street, Odonla, Ikorodu, Lagos State. Nigeria
              </p>
            </div>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold mb-4">15. CONSENT</h2>
            <p className="text-muted-foreground leading-relaxed">
              By using the Platform, you consent to the collection, use, and disclosure of your personal information as described in this Privacy Policy.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3 font-medium">
              By continuing to use NEXTGIG you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </section>

        </div>
      </div>
    </Layout>
  );
}
